// Phase 11A (card3226/AXREG-1481): canonical persisted formats are constrained to what the
// backend TCLE sanitizer preserves at HEAD (allowedTags: p, b, strong, i, br). Italic is
// therefore persisted as <i> (not <em>, which the backend strips). Strikethrough has no
// backend-preservable tag, so s/strike/del are intentionally absent here: they fall through
// to the generic inline path, which keeps their TEXT and drops the strike formatting.
const SUPPORTED_INLINE_TAGS = {
  B: 'strong',
  STRONG: 'strong',
  I: 'i',
  EM: 'i',
};

const BLOCK_TAGS = new Set([
  'ADDRESS', 'ARTICLE', 'BLOCKQUOTE', 'DIV', 'FOOTER', 'H1', 'H2', 'H3',
  'H4', 'H5', 'H6', 'HEADER', 'LI', 'P', 'PRE', 'SECTION', 'TABLE', 'TR',
]);

const DANGEROUS_TAGS = new Set([
  'APPLET', 'AUDIO', 'BUTTON', 'CANVAS', 'EMBED', 'FORM', 'IFRAME', 'IMG',
  'INPUT', 'MATH', 'META', 'OBJECT', 'SCRIPT', 'SELECT', 'STYLE', 'SVG',
  'TEXTAREA', 'VIDEO',
]);

const ALLOWED_CLIPBOARD_ATTRIBUTES = ['bold', 'italic'];

export const TCLE_CHARACTER_LIMIT = 10000;
export const TCLE_LAYOUT_SAFETY_GAP_PX = 16;
export const TCLE_MEASUREMENT_CANVAS_WIDTH_PX = 952;
export const TCLE_PREVIEW_MODAL_MAX_WIDTH_PX = 1000;
export const TCLE_PREVIEW_MODAL_VIEWPORT_GUTTER_PX = 32;
export const TCLE_PREVIEW_FONT_STYLESHEET_URL = 'https://fonts.googleapis.com/css2?family=Roboto+Condensed:wght@400;700&display=swap';

const TCLE_PREVIEW_FONT_LINK_ATTRIBUTE = 'data-card3226-tcle-fonts';
const tclePreviewFontPromises = new WeakMap();

export function ensureTclePreviewFonts(documentRef = document) {
  if (!documentRef || !documentRef.head || typeof documentRef.createElement !== 'function') {
    return Promise.resolve();
  }

  const existingPromise = tclePreviewFontPromises.get(documentRef);
  if (existingPromise) return existingPromise;

  let stylesheet = documentRef.head.querySelector(`link[${TCLE_PREVIEW_FONT_LINK_ATTRIBUTE}]`);
  let resolveStylesheet;
  const stylesheetReady = new Promise((resolve) => { resolveStylesheet = resolve; });

  if (!stylesheet) {
    stylesheet = documentRef.createElement('link');
    stylesheet.rel = 'stylesheet';
    stylesheet.href = TCLE_PREVIEW_FONT_STYLESHEET_URL;
    stylesheet.setAttribute(TCLE_PREVIEW_FONT_LINK_ATTRIBUTE, '');
    stylesheet.addEventListener('load', resolveStylesheet, { once: true });
    stylesheet.addEventListener('error', resolveStylesheet, { once: true });
    documentRef.head.appendChild(stylesheet);
  } else if (stylesheet.sheet) {
    resolveStylesheet();
  } else {
    stylesheet.addEventListener('load', resolveStylesheet, { once: true });
    stylesheet.addEventListener('error', resolveStylesheet, { once: true });
  }

  const fontPromise = stylesheetReady.then(() => {
    if (!documentRef.fonts || typeof documentRef.fonts.load !== 'function') return undefined;
    return Promise.all([
      documentRef.fonts.load('400 12px "Roboto Condensed"', 'TCLE'),
      documentRef.fonts.load('700 12px "Roboto Condensed"', 'TCLE'),
    ]).catch(() => undefined);
  });

  tclePreviewFontPromises.set(documentRef, fontPromise);
  return fontPromise;
}

export function createTcleMeasurementRoot(host) {
  if (!host || typeof host.attachShadow !== 'function') return null;
  return host.shadowRoot || host.attachShadow({ mode: 'open' });
}

export function getTclePreviewModalWidth(viewportWidth) {
  const numericViewportWidth = Number(viewportWidth);
  if (!Number.isFinite(numericViewportWidth)) return TCLE_PREVIEW_MODAL_MAX_WIDTH_PX;

  return Math.min(
    TCLE_PREVIEW_MODAL_MAX_WIDTH_PX,
    Math.max(0, numericViewportWidth - TCLE_PREVIEW_MODAL_VIEWPORT_GUTTER_PX),
  );
}

export function renderTcleMeasurementHtml(root, html) {
  if (!root) return false;
  root.innerHTML = html || '';
  return true;
}

function hasMeaningfulContent(element) {
  return element.textContent.length > 0 || element.querySelector('br') !== null;
}

function hasInlineAncestor(target, tagName) {
  let current = target;
  while (current) {
    if (current.tagName && current.tagName.toLowerCase() === tagName) return true;
    current = current.parentElement;
  }
  return false;
}

function getSafeInlineStyleTags(source) {
  if (!source.style) return [];
  const fontStyle = String(source.style.fontStyle || '').toLowerCase();
  const fontWeight = String(source.style.fontWeight || '').toLowerCase();
  const numericFontWeight = Number.parseInt(fontWeight, 10);
  const tags = [];

  if (fontWeight === 'bold' || fontWeight === 'bolder'
    || (Number.isFinite(numericFontWeight) && numericFontWeight >= 600)) {
    tags.push('strong');
  }
  if (fontStyle === 'italic' || fontStyle === 'oblique') tags.push('i');
  return tags;
}

function getSupportedInlineTag(source) {
  const supportedTag = SUPPORTED_INLINE_TAGS[source.tagName];
  if (!supportedTag || !source.style) return supportedTag;

  const fontStyle = String(source.style.fontStyle || '').toLowerCase();
  const fontWeight = String(source.style.fontWeight || '').toLowerCase();
  const numericFontWeight = Number.parseInt(fontWeight, 10);

  if (supportedTag === 'strong'
    && (fontWeight === 'normal'
      || (Number.isFinite(numericFontWeight) && numericFontWeight < 600))) {
    return null;
  }
  if (supportedTag === 'i' && fontStyle === 'normal') return null;
  return supportedTag;
}

function appendStructuralBreak(target, documentRef) {
  if (target.lastChild && target.lastChild.nodeName === 'BR') return;
  target.appendChild(documentRef.createElement('br'));
}

function appendInline(source, target, documentRef) {
  if (source.nodeType === 3) {
    target.appendChild(documentRef.createTextNode(source.nodeValue));
    return;
  }

  if (source.nodeType !== 1 || DANGEROUS_TAGS.has(source.tagName)) return;

  if (source.tagName === 'BR') {
    target.appendChild(documentRef.createElement('br'));
    return;
  }

  const supportedTag = getSupportedInlineTag(source);
  if (supportedTag) {
    const inline = documentRef.createElement(supportedTag);
    Array.from(source.childNodes).forEach((child) => appendInline(child, inline, documentRef));

    if (!inline.hasChildNodes()) return;
    if (target.tagName && target.tagName.toLowerCase() === supportedTag) {
      Array.from(inline.childNodes).forEach((child) => target.appendChild(child));
    } else {
      target.appendChild(inline);
    }
    return;
  }

  const styleTags = getSafeInlineStyleTags(source);
  if (styleTags.length) {
    const missingTags = styleTags.filter(
      (tagName) => !hasInlineAncestor(target, tagName),
    );
    if (!missingTags.length) {
      Array.from(source.childNodes).forEach((child) => appendInline(child, target, documentRef));
      return;
    }

    let outer = null;
    let inner = null;
    missingTags.forEach((tagName) => {
      const wrapper = documentRef.createElement(tagName);
      if (inner) inner.appendChild(wrapper);
      else outer = wrapper;
      inner = wrapper;
    });
    Array.from(source.childNodes).forEach((child) => appendInline(child, inner, documentRef));
    if (outer && outer.hasChildNodes()) target.appendChild(outer);
    return;
  }

  const isNestedBlock = BLOCK_TAGS.has(source.tagName);
  if (isNestedBlock && hasMeaningfulContent(target)) {
    appendStructuralBreak(target, documentRef);
  }

  Array.from(source.childNodes).forEach((child) => appendInline(child, target, documentRef));

  if (isNestedBlock && hasMeaningfulContent(target)) {
    appendStructuralBreak(target, documentRef);
  }
}

function appendParagraph(output, sourceNodes, documentRef) {
  const paragraph = documentRef.createElement('p');
  sourceNodes.forEach((node) => appendInline(node, paragraph, documentRef));

  while (paragraph.lastChild && paragraph.lastChild.nodeName === 'BR'
    && paragraph.childNodes.length > 1) {
    paragraph.removeChild(paragraph.lastChild);
  }

  if (!hasMeaningfulContent(paragraph)) paragraph.appendChild(documentRef.createElement('br'));
  output.appendChild(paragraph);
}

export function canonicalizeTcleHtml(value) {
  const parser = new DOMParser();
  const parsed = parser.parseFromString(value || '', 'text/html');
  const output = parsed.createElement('div');
  let inlineBuffer = [];

  const flushInlineBuffer = () => {
    if (!inlineBuffer.length) return;
    const onlyFormattingWhitespace = inlineBuffer.every(
      (node) => node.nodeType === 3 && node.nodeValue.trim() === '',
    );
    if (!onlyFormattingWhitespace) appendParagraph(output, inlineBuffer, parsed);
    inlineBuffer = [];
  };

  Array.from(parsed.body.childNodes).forEach((node) => {
    if (node.nodeType === 1 && BLOCK_TAGS.has(node.tagName)) {
      flushInlineBuffer();
      appendParagraph(output, Array.from(node.childNodes), parsed);
      return;
    }
    inlineBuffer.push(node);
  });
  flushInlineBuffer();

  return output.innerHTML || '<p><br></p>';
}

export function createTcleQuillLoadValue(value, convert) {
  const canonicalValue = canonicalizeTcleHtml(value);
  if (typeof convert !== 'function') return canonicalValue;

  const parsed = new DOMParser().parseFromString(canonicalValue, 'text/html');
  const paragraphs = Array.from(parsed.body.querySelectorAll('p'));
  const trailingParagraph = paragraphs[paragraphs.length - 1];
  const hasMeaningfulTrailingBlank = paragraphs.length > 1
    && trailingParagraph
    && trailingParagraph.textContent.length === 0
    && trailingParagraph.querySelector('br') !== null;

  if (!hasMeaningfulTrailingBlank) return canonicalValue;

  const converted = convert(canonicalValue);
  if (!converted || typeof converted.insert !== 'function') return canonicalValue;

  // ReactQuill loads string values through Quill clipboard.convert(). Quill 1.3 removes
  // the final unformatted newline as its editor sentinel, which otherwise consumes a real
  // trailing blank paragraph. Supplying a Delta with that one newline restored preserves
  // the persisted paragraph while leaving ordinary documents on the existing string path.
  return converted.insert('\n');
}

export function getTclePlainText(value) {
  const parsed = new DOMParser().parseFromString(canonicalizeTcleHtml(value), 'text/html');

  const extractText = (node) => {
    if (node.nodeType === 3) return node.nodeValue;
    if (node.nodeType !== 1) return '';
    if (node.tagName === 'BR') return '\n';
    return Array.from(node.childNodes).map(extractText).join('');
  };

  return Array.from(parsed.body.querySelectorAll('p'))
    .map((paragraph) => extractText(paragraph))
    .join('\n');
}

export function getTcleCharacterBreakdown(value) {
  const canonicalValue = canonicalizeTcleHtml(value);
  const characters = Array.from(getTclePlainText(canonicalValue));
  if (canonicalValue === '<p><br></p>') {
    return { lineBreaks: 0, textCharacters: 0, total: 0 };
  }
  const lineBreaks = characters.reduce(
    (total, character) => total + (character === '\n' ? 1 : 0),
    0,
  );
  return {
    lineBreaks,
    textCharacters: characters.length - lineBreaks,
    total: characters.length,
  };
}

export function getTcleCharacterCount(value) {
  return getTcleCharacterBreakdown(value).total;
}

/**
 * Occupancy of the custom text area, excluding the header and everything after the text
 * (including signatures and their spacing). Informational only: geometry still gates Save.
 */
export function getTclePageUsagePercent(layoutMetrics) {
  if (!layoutMetrics) return null;

  const {
    customTop, customBottom, pageBottom, protectedContentBottom,
  } = layoutMetrics;
  if (![customTop, customBottom, pageBottom, protectedContentBottom].every(Number.isFinite)) {
    return null;
  }
  const usedHeight = customBottom - customTop;
  const reservedBelow = protectedContentBottom - customBottom;
  const availableHeight = pageBottom - customTop - reservedBelow;
  if (usedHeight < 0 || reservedBelow < 0 || availableHeight <= 0) return null;

  const percent = (usedHeight / availableHeight) * 100;
  // Do not round a small overrun back to 100%, or a nearly full area up to 100%.
  return percent > 100 ? Math.ceil(percent) : Math.floor(percent);
}

/**
 * Prefer the blocking dimension to a misleading vertical percentage. Never expose metrics
 * from the previous edit while a new measurement is pending.
 */
export function getTcleLayoutFeedback(layoutMetrics, layoutStatus = 'valid') {
  if (layoutStatus === 'pending') return { kind: 'pending', percent: null };
  if (!layoutMetrics) return null;
  if (layoutMetrics.horizontalOverflow === true) return { kind: 'width', percent: null };
  if (Number.isFinite(layoutMetrics.gap) && Number.isFinite(layoutMetrics.safetyGap)
    && layoutMetrics.gap < layoutMetrics.safetyGap) return { kind: 'spacing', percent: null };

  const percent = getTclePageUsagePercent(layoutMetrics);
  if (percent === null) return null;

  return { kind: 'height', percent };
}

export function getTcleContentValidation(value, geometryValid = true) {
  const plainText = getTclePlainText(value);
  const used = getTcleCharacterBreakdown(value).total;
  const overCharacterLimit = used > TCLE_CHARACTER_LIMIT;
  const empty = plainText.trim().length === 0;

  return {
    empty,
    geometryValid,
    overCharacterLimit,
    remaining: TCLE_CHARACTER_LIMIT - used,
    used,
    valid: !empty && !overCharacterLimit && geometryValid,
  };
}

export function getTclePasteCharacterBudget(
  value,
  selectedCharacters = 0,
  maximumCharacters = TCLE_CHARACTER_LIMIT,
) {
  const validation = getTcleContentValidation(value);
  const currentCharacters = validation.empty ? 0 : validation.used;
  const replaceableCharacters = Math.min(
    Math.max(0, selectedCharacters),
    currentCharacters,
  );
  return Math.max(0, maximumCharacters - (currentCharacters - replaceableCharacters));
}

export function sanitizeTcleClipboardDelta(node, delta) {
  const SanitizedDelta = delta.constructor;
  return delta.ops.reduce((sanitized, operation) => {
    if (typeof operation.insert !== 'string') return sanitized;

    const attributes = ALLOWED_CLIPBOARD_ATTRIBUTES.reduce((allowed, attribute) => {
      if (operation.attributes && operation.attributes[attribute] === true) {
        allowed[attribute] = true;
      }
      return allowed;
    }, {});

    return sanitized.insert(
      operation.insert,
      Object.keys(attributes).length ? attributes : undefined,
    );
  }, new SanitizedDelta());
}

export function truncateTcleClipboardDelta(delta, maximumCharacters) {
  const TruncatedDelta = delta.constructor;
  const output = new TruncatedDelta();
  const limit = Math.max(0, Number.isFinite(maximumCharacters) ? maximumCharacters : 0);
  let accepted = 0;
  let total = 0;

  delta.ops.forEach((operation) => {
    if (typeof operation.insert !== 'string') return;
    const characters = Array.from(operation.insert);
    total += characters.length;
    const available = Math.max(0, limit - accepted);
    if (available === 0) return;

    const acceptedText = characters.slice(0, available).join('');
    if (acceptedText) output.insert(acceptedText, operation.attributes);
    accepted += Math.min(characters.length, available);
  });

  return {
    accepted,
    delta: output,
    discarded: total - accepted,
    total,
    truncated: accepted < total,
  };
}

export function discardTcleClipboardDelta(node, delta) {
  const EmptyDelta = delta.constructor;
  return new EmptyDelta();
}

function applyTcleClipboardFormat(delta, format) {
  const FormattedDelta = delta.constructor;
  return delta.ops.reduce((formatted, operation) => {
    if (typeof operation.insert !== 'string') return formatted;
    return formatted.insert(operation.insert, {
      ...(operation.attributes || {}),
      [format]: true,
    });
  }, new FormattedDelta());
}

export function preserveTcleClipboardBold(node, delta) {
  return applyTcleClipboardFormat(delta, 'bold');
}

export function preserveTcleClipboardItalic(node, delta) {
  return applyTcleClipboardFormat(delta, 'italic');
}

export function inspectTcleClipboardHtml(value) {
  if (!value) return { dangerousContent: false, unsupportedFormatting: false };

  const parsed = new DOMParser().parseFromString(value, 'text/html');
  const elements = Array.from(parsed.body.querySelectorAll('*'));
  return {
    dangerousContent: elements.some((element) => DANGEROUS_TAGS.has(element.tagName)
      || Array.from(element.attributes).some((attribute) => /^on/i.test(attribute.name))),
    unsupportedFormatting: elements.some((element) => {
      const supportedTag = SUPPORTED_INLINE_TAGS[element.tagName]
        || BLOCK_TAGS.has(element.tagName)
        || element.tagName === 'BR';
      return !supportedTag || Array.from(element.attributes).some((attribute) => (
        attribute.name === 'style'
        || attribute.name === 'class'
        || attribute.name === 'color'
        || attribute.name === 'size'
        || attribute.name === 'face'
      ));
    }),
  };
}

export function getTcleSaveErrorDetails(error) {
  const status = error && (error.status || (error.response && error.response.status));
  const headers = (error && (error.headers || (error.response && error.response.headers))) || {};
  const correlationId = headers['x-correlation-id'] || headers['x-request-id'] || headers['x-trace-id'];
  let category = 'network';

  if (status === 400 || status === 422) category = 'validation';
  else if (status === 401 || status === 403) category = 'authorization';
  else if (status === 409) category = 'conflict';
  else if (status === 413) category = 'payload_too_large';
  else if (status >= 500) category = 'server';
  else if (status) category = 'http';

  const descriptions = {
    authorization: 'Sua sessão não permite concluir esta alteração. Atualize a página e tente novamente.',
    conflict: 'O termo foi alterado em outro fluxo. Atualize a página antes de tentar novamente.',
    network: 'Não foi possível obter resposta do servidor. Verifique sua conexão e tente novamente.',
    payload_too_large: 'O servidor recusou o tamanho do conteúdo. O termo foi mantido no editor.',
    server: 'O servidor não conseguiu concluir a alteração. O termo foi mantido no editor.',
    validation: 'O servidor não aceitou o conteúdo enviado. Revise o termo e tente novamente.',
    http: 'A alteração não foi concluída. O termo foi mantido no editor.',
  };

  return {
    category,
    status: status || null,
    correlationId: correlationId || null,
    userDescription: descriptions[category],
  };
}

export async function saveCanonicalTcle(value, save) {
  const canonicalValue = canonicalizeTcleHtml(value);
  await save(canonicalValue);
  return canonicalValue;
}
